/**
 * Token Spend section — cost-led KPI row, daily spend trend, model/label/user
 * breakdowns. All data comes from the Langfuse-backed token-consumption API.
 */
import { useMemo, type ComponentProps } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { TokenConsumptionResponse } from '@/services/composer-dashboard/get-token-consumption';
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Cell,
} from 'recharts';
import {
  type ChartConfig,
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  ChartLegend,
} from '@/components/ui/chart';
import { CalendarIcon, CalendarDays, Coins, Gauge, Users } from 'lucide-react';
import { format } from 'date-fns';
import { type DateRange } from 'react-day-picker';
import { SectionHeading } from './section-heading';
import {
  cleanModelName,
  getChartPaletteColor,
  getModelColor,
} from './helpers/model-colors';
import { ChartCategoryAxisTick } from './helpers/chart-category-axis-tick';
import { TruncateTextWithTooltip } from '@/components/ui/truncate-text-with-tooltip';

function formatTokens(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return String(n);
}

function formatUsd(n: number, fractionDigits = 2): string {
  return `$${n.toLocaleString(undefined, {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  })}`;
}

function formatDateRange(range: DateRange | undefined): string {
  if (!range?.from) return 'All time';
  const from = format(range.from, 'MM/dd/yyyy');
  if (!range.to || range.from.getTime() === range.to.getTime()) return from;
  return `${from} – ${format(range.to, 'MM/dd/yyyy')}`;
}

const PRESET_DAY_OPTIONS = [
  { label: '7d', days: 7 },
  { label: '30d', days: 30 },
  { label: '90d', days: 90 },
] as const;

type DailyRow = {
  date: string;
  label: string;
  totalCost: number;
  [modelName: string]: number | string;
};

const LABEL_BAR_DIM_OPACITY = 0.28;
const CATEGORY_AXIS_WIDTH = 160;
/** Models with less than this total cost are omitted from the spend-over-time chart. */
const MIN_TIMESERIES_MODEL_COST_USD = 0.01;
/** Max models in the stacked area chart to keep legend and tooltip readable. */
const MAX_TIMESERIES_MODELS = 8;
/** Show every other Y-axis label when the model bar chart exceeds this count. */
const MODEL_AXIS_LABEL_INTERVAL_THRESHOLD = 10;

function SpendAreaTooltipContent({
  active,
  payload,
  label,
}: {
  active?: boolean;
  payload?: ReadonlyArray<{
    value?: number | string;
    name?: string;
    dataKey?: string;
    color?: string;
    payload?: Record<string, unknown>;
  }>;
  label?: string | number;
}) {
  const filtered = payload?.filter((item) => Number(item.value ?? 0) >= 0.005);

  if (!active || !filtered?.length) {
    return null;
  }

  return (
    <ChartTooltipContent
      active={active}
      payload={[...filtered]}
      label={label}
      formatter={(value) => formatUsd(Number(value))}
      className='z-50'
    />
  );
}

function SpendChartLegend({
  payload,
}: {
  payload?: Array<{ value?: string; color?: string }>;
}) {
  if (!payload?.length) return null;
  return (
    <div className='flex flex-wrap justify-center gap-x-3 gap-y-1 pt-3'>
      {payload.map((item) => (
        <div
          key={item.value}
          className='flex min-w-0 max-w-[150px] items-center gap-1.5'
        >
          <div
            className='size-2 shrink-0 rounded-sm'
            style={{ backgroundColor: item.color }}
          />
          <TruncateTextWithTooltip
            text={item.value ?? ''}
            className='text-xs text-muted-foreground'
          />
        </div>
      ))}
    </div>
  );
}

interface TokenConsumptionSectionPreviewProps {
  metrics: TokenConsumptionResponse | undefined;
  isLoading?: boolean;
  error?: Error | null;
  dateRange?: DateRange;
  onDateRangeChange?: (range: DateRange | undefined) => void;
  selectedLabelIds?: string[];
  onToggleLabel?: (labelId: string) => void;
}

export function TokenConsumptionSectionPreview({
  metrics,
  isLoading = false,
  error,
  dateRange,
  onDateRangeChange,
  selectedLabelIds = [],
  onToggleLabel,
}: TokenConsumptionSectionPreviewProps) {
  const modelBarData = useMemo(
    () =>
      metrics?.top_models.map((m) => {
        const name = cleanModelName(m.model);
        return { name, tokens: m.tokens, fill: getModelColor(name) };
      }) ?? [],
    [metrics],
  );

  const barChartConfig = useMemo(() => {
    const config: ChartConfig = { tokens: { label: 'Tokens' } };
    modelBarData.forEach((i) => {
      config[i.name] = { label: i.name, color: i.fill };
    });
    return config;
  }, [modelBarData]);

  const timeseries = useMemo(() => {
    const realSeries = metrics?.series;
    if (!realSeries?.length) return null;

    const rawModelNames = Array.from(
      new Set(realSeries.flatMap((pt) => Object.keys(pt.by_model))),
    )
      .map((rawName) => ({
        rawName,
        totalCost: realSeries.reduce(
          (sum, pt) => sum + (pt.by_model[rawName]?.cost_usd ?? 0),
          0,
        ),
      }))
      .filter(({ totalCost }) => totalCost >= MIN_TIMESERIES_MODEL_COST_USD)
      .sort((a, b) => b.totalCost - a.totalCost)
      .slice(0, MAX_TIMESERIES_MODELS)
      .map(({ rawName }) => rawName);

    if (rawModelNames.length === 0) return null;

    const rawToClean = Object.fromEntries(
      rawModelNames.map((raw) => [raw, cleanModelName(raw)]),
    );
    const cleanedModelNames = Array.from(new Set(Object.values(rawToClean)));

    const rows = realSeries.map((pt) => {
      const row: DailyRow = {
        date: pt.date,
        label: pt.date.slice(5),
        totalCost: rawModelNames.reduce(
          (acc, raw) => acc + (pt.by_model[raw]?.cost_usd ?? 0),
          0,
        ),
      };
      rawModelNames.forEach((raw) => {
        const clean = rawToClean[raw];
        row[clean] =
          ((row[clean] as number) ?? 0) + (pt.by_model[raw]?.cost_usd ?? 0);
      });
      return row;
    });

    return { rows, modelNames: cleanedModelNames };
  }, [metrics]);

  const modelAxisLabelInterval =
    modelBarData.length > MODEL_AXIS_LABEL_INTERVAL_THRESHOLD ? 1 : 0;

  const areaChartConfig = useMemo(() => {
    const config: ChartConfig = {};
    timeseries?.modelNames.forEach((n) => {
      config[n] = { label: n, color: getModelColor(n) };
    });
    return config;
  }, [timeseries]);

  const labelSpendData = useMemo(() => {
    if (!metrics?.by_label?.length) return [];
    return metrics.by_label
      .filter((item) => item.total_cost_usd > 0)
      .sort((a, b) => b.total_cost_usd - a.total_cost_usd)
      .slice(0, 8)
      .map((item, idx) => ({
        id: item.label_id,
        name: item.label_name,
        cost: item.total_cost_usd,
        color: getChartPaletteColor(idx),
      }));
  }, [metrics]);

  const labelSpendConfig = useMemo(() => {
    const config: ChartConfig = { cost: { label: 'Spend' } };
    labelSpendData.forEach((d) => {
      config[d.name] = { label: d.name, color: d.color };
    });
    return config;
  }, [labelSpendData]);

  const selectedLabelSet = useMemo(
    () => new Set(selectedLabelIds),
    [selectedLabelIds],
  );

  if (isLoading) {
    return (
      <div className='space-y-4'>
        <Skeleton className='h-8 w-64' />
        <div className='grid grid-cols-1 gap-4 md:grid-cols-3'>
          {Array.from({ length: 3 }).map((_, i) => (
            <Card key={i}>
              <CardContent className='pt-6'>
                <Skeleton className='h-16 w-full' />
              </CardContent>
            </Card>
          ))}
        </div>
        <Card>
          <CardContent className='pt-6'>
            <Skeleton className='h-[320px] w-full' />
          </CardContent>
        </Card>
        <div className='grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3'>
          {Array.from({ length: 3 }).map((_, i) => (
            <Card key={i}>
              <CardContent className='pt-6'>
                <Skeleton className='h-[250px] w-full' />
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  if (error || !metrics) {
    return (
      <div className='space-y-4'>
        <SectionHeading>Token Spend</SectionHeading>
        <div className='flex h-[120px] items-center justify-center text-muted-foreground'>
          <p className='text-sm'>
            {error?.message || 'No token consumption data available'}
          </p>
        </div>
      </div>
    );
  }

  const langfuseAvailable = metrics.langfuse_available !== false;
  const hasCustomRange = !!dateRange?.from;
  const rangeLabel = hasCustomRange ? formatDateRange(dateRange) : 'All time';

  const rangeDays = (() => {
    if (dateRange?.from && dateRange?.to) {
      return Math.max(
        1,
        Math.round(
          (dateRange.to.getTime() - dateRange.from.getTime()) / 86400000,
        ) + 1,
      );
    }
    if (metrics.start_date && metrics.end_date) {
      return Math.max(
        1,
        Math.round(
          (Date.parse(metrics.end_date) - Date.parse(metrics.start_date)) /
            86400000,
        ) + 1,
      );
    }
    return null;
  })();

  const dailyAvgCost =
    rangeDays != null ? metrics.total_cost_usd / rangeDays : null;
  const dailyAvgTokens =
    rangeDays != null ? metrics.total_tokens / rangeDays : null;

  const periodTotalLabel = (() => {
    if (!dateRange?.from) return 'All-Time Spend';
    const now = new Date();
    for (const { days } of PRESET_DAY_OPTIONS) {
      const presetFrom = new Date();
      presetFrom.setDate(presetFrom.getDate() - (days - 1));
      if (
        Math.abs(dateRange.from.getTime() - presetFrom.getTime()) < 86400000 &&
        dateRange.to &&
        Math.abs(dateRange.to.getTime() - now.getTime()) < 86400000
      ) {
        return `${days}-Day Spend`;
      }
    }
    return rangeDays != null ? `${rangeDays}-Day Spend` : 'Custom Range Spend';
  })();

  const costPerMillionTokens =
    langfuseAvailable && metrics.total_tokens > 0
      ? (metrics.total_cost_usd * 1_000_000) / metrics.total_tokens
      : null;

  return (
    <div className='space-y-4'>
      <div className='flex flex-wrap items-center justify-between gap-2'>
        <SectionHeading>Token Spend</SectionHeading>
        {onDateRangeChange && (
          <div className='flex items-center gap-2'>
            {PRESET_DAY_OPTIONS.map(({ label, days: d }) => {
              const presetFrom = new Date();
              presetFrom.setDate(presetFrom.getDate() - (d - 1));
              const isActive =
                dateRange?.from &&
                dateRange?.to &&
                Math.abs(dateRange.from.getTime() - presetFrom.getTime()) <
                  86400000 &&
                Math.abs(dateRange.to.getTime() - new Date().getTime()) <
                  86400000;
              return (
                <Button
                  key={label}
                  variant={isActive ? 'default' : 'outline'}
                  size='sm'
                  onClick={() => {
                    const from = new Date();
                    from.setDate(from.getDate() - (d - 1));
                    onDateRangeChange({ from, to: new Date() });
                  }}
                >
                  {label}
                </Button>
              );
            })}
            <Button
              variant={!dateRange?.from ? 'default' : 'outline'}
              size='sm'
              onClick={() => onDateRangeChange(undefined)}
            >
              All time
            </Button>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant='outline'
                  size='sm'
                  className='w-[200px] justify-between text-left font-normal'
                >
                  {hasCustomRange ? formatDateRange(dateRange) : 'Custom range'}
                  <CalendarIcon className='ml-2 size-4' />
                </Button>
              </PopoverTrigger>
              <PopoverContent className='w-auto p-0' align='end'>
                <Calendar
                  disabled={{ after: new Date() }}
                  mode='range'
                  selected={dateRange}
                  onSelect={(v: DateRange | undefined) => onDateRangeChange(v)}
                  initialFocus
                />
              </PopoverContent>
            </Popover>
          </div>
        )}
      </div>

      {!langfuseAvailable && (
        <p className='text-sm text-muted-foreground'>
          Spend data is temporarily unavailable.
        </p>
      )}

      <div className='grid grid-cols-1 gap-4 md:grid-cols-3'>
        <Card>
          <CardHeader className='flex flex-row items-center justify-between space-y-0 pb-2'>
            <CardTitle className='text-sm font-medium'>Daily Average</CardTitle>
            <CalendarDays className='size-4 text-muted-foreground' />
          </CardHeader>
          <CardContent>
            <div className='text-2xl font-bold'>
              {langfuseAvailable && dailyAvgCost != null
                ? formatUsd(dailyAvgCost)
                : '—'}
            </div>
            <p className='text-xs text-muted-foreground'>
              {langfuseAvailable && dailyAvgTokens != null && rangeDays != null
                ? `${formatTokens(dailyAvgTokens)} tokens · over ${rangeDays} day${rangeDays === 1 ? '' : 's'}`
                : '\u00A0'}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className='flex flex-row items-center justify-between space-y-0 pb-2'>
            <CardTitle className='text-sm font-medium'>
              {periodTotalLabel}
            </CardTitle>
            <Coins className='size-4 text-muted-foreground' />
          </CardHeader>
          <CardContent>
            <div className='text-2xl font-bold'>
              {langfuseAvailable ? formatUsd(metrics.total_cost_usd, 0) : '—'}
            </div>
            <p className='text-xs text-muted-foreground'>
              {langfuseAvailable
                ? `${formatTokens(metrics.total_tokens)} tokens`
                : '\u00A0'}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className='flex flex-row items-center justify-between space-y-0 pb-2'>
            <CardTitle className='text-sm font-medium'>
              Cost per 1M Tokens
            </CardTitle>
            <Gauge className='size-4 text-muted-foreground' />
          </CardHeader>
          <CardContent>
            <div className='text-2xl font-bold'>
              {costPerMillionTokens != null
                ? formatUsd(costPerMillionTokens)
                : '—'}
            </div>
            <p className='text-xs text-muted-foreground'>
              {langfuseAvailable ? 'blended model-mix rate' : '\u00A0'}
            </p>
          </CardContent>
        </Card>
      </div>

      {langfuseAvailable && timeseries && timeseries.modelNames.length > 0 && (
        <Card>
          <CardHeader className='pb-2'>
            <CardTitle className='text-base'>Spend over time</CardTitle>
            <p className='mt-1 text-xs text-muted-foreground'>
              Daily cost stacked by top {MAX_TIMESERIES_MODELS} models ·{' '}
              {rangeLabel.toLowerCase()}
            </p>
          </CardHeader>
          <CardContent>
            <ChartContainer
              config={areaChartConfig}
              className='h-[300px] w-full [&_.recharts-tooltip-wrapper]:z-50'
            >
              <AreaChart data={timeseries.rows}>
                <defs>
                  {timeseries.modelNames.map((n) => {
                    const color = getModelColor(n);
                    return (
                      <linearGradient
                        key={n}
                        id={`area-${n}`}
                        x1='0'
                        y1='0'
                        x2='0'
                        y2='1'
                      >
                        <stop offset='5%' stopColor={color} stopOpacity={0.7} />
                        <stop
                          offset='95%'
                          stopColor={color}
                          stopOpacity={0.1}
                        />
                      </linearGradient>
                    );
                  })}
                </defs>
                <CartesianGrid strokeDasharray='3 3' vertical={false} />
                <XAxis
                  dataKey='label'
                  tickLine={false}
                  axisLine={false}
                  tick={{ fontSize: 11 }}
                  minTickGap={24}
                />
                <YAxis
                  tickLine={false}
                  axisLine={false}
                  tick={{ fontSize: 11 }}
                  tickFormatter={(v) =>
                    `$${Number(v).toFixed(Number(v) < 1 ? 2 : 0)}`
                  }
                  width={48}
                />
                <ChartTooltip
                  wrapperStyle={{ zIndex: 50 }}
                  content={(props) => (
                    <SpendAreaTooltipContent
                      {...(props as ComponentProps<
                        typeof SpendAreaTooltipContent
                      >)}
                    />
                  )}
                />
                <ChartLegend
                  wrapperStyle={{ zIndex: 1 }}
                  content={<SpendChartLegend />}
                />
                {timeseries.modelNames.map((n) => (
                  <Area
                    key={n}
                    type='monotone'
                    dataKey={n}
                    stackId='1'
                    stroke={getModelColor(n)}
                    fill={`url(#area-${n})`}
                    strokeWidth={1.5}
                  />
                ))}
              </AreaChart>
            </ChartContainer>
          </CardContent>
        </Card>
      )}

      <div className='grid grid-cols-1 items-stretch gap-4 md:grid-cols-2 xl:grid-cols-3'>
        {langfuseAvailable && modelBarData.length > 0 && (
          <Card className='flex h-full flex-col'>
            <CardHeader>
              <CardTitle className='text-base'>Token Usage by Model</CardTitle>
            </CardHeader>
            <CardContent className='flex min-h-0 flex-1 flex-col'>
              <ChartContainer
                config={barChartConfig}
                className='aspect-auto! min-h-[250px] w-full flex-1'
              >
                <BarChart
                  data={modelBarData}
                  layout='vertical'
                  margin={{ top: 8, right: 16, left: 0, bottom: 8 }}
                >
                  <CartesianGrid horizontal={false} strokeDasharray='3 3' />
                  <YAxis
                    type='category'
                    dataKey='name'
                    width={CATEGORY_AXIS_WIDTH}
                    interval={modelAxisLabelInterval}
                    tickLine={false}
                    axisLine={false}
                    tick={(props) => (
                      <ChartCategoryAxisTick
                        {...props}
                        tickWidth={CATEGORY_AXIS_WIDTH - 8}
                      />
                    )}
                  />
                  <XAxis
                    type='number'
                    tickFormatter={formatTokens}
                    tickLine={false}
                    axisLine={false}
                    domain={[0, 'dataMax']}
                  />
                  <ChartTooltip
                    content={
                      <ChartTooltipContent
                        formatter={(v) => formatTokens(v as number)}
                      />
                    }
                  />
                  <Bar
                    dataKey='tokens'
                    radius={[0, 4, 4, 0]}
                    minPointSize={(v) => (v && v > 0 ? 5 : 0)}
                  >
                    {modelBarData.map((entry) => (
                      <Cell key={entry.name} fill={entry.fill} />
                    ))}
                  </Bar>
                </BarChart>
              </ChartContainer>
            </CardContent>
          </Card>
        )}

        {langfuseAvailable && (
          <Card className='flex h-full flex-col'>
            <CardHeader className='min-h-[4.5rem] pb-2'>
              <CardTitle className='text-base'>Spend by Label</CardTitle>
              <p className='text-xs text-muted-foreground'>
                Click a bar to drill into that label.
              </p>
            </CardHeader>
            <CardContent className='flex min-h-0 flex-1 flex-col'>
              {labelSpendData.length > 0 ? (
                <ChartContainer
                  config={labelSpendConfig}
                  className='aspect-auto! min-h-[250px] w-full flex-1'
                >
                  <BarChart
                    data={labelSpendData}
                    layout='vertical'
                    margin={{ top: 4, right: 24, bottom: 4, left: 4 }}
                  >
                    <CartesianGrid horizontal={false} strokeDasharray='3 3' />
                    <YAxis
                      type='category'
                      dataKey='name'
                      width={CATEGORY_AXIS_WIDTH}
                      interval={0}
                      tickLine={false}
                      axisLine={false}
                      tick={(props) => (
                        <ChartCategoryAxisTick
                          {...props}
                          tickWidth={CATEGORY_AXIS_WIDTH - 8}
                        />
                      )}
                    />
                    <XAxis
                      type='number'
                      tickLine={false}
                      axisLine={false}
                      domain={[0, 'dataMax']}
                      tickFormatter={(v) =>
                        `$${Number(v).toFixed(Number(v) < 10 ? 2 : 0)}`
                      }
                    />
                    <ChartTooltip
                      content={
                        <ChartTooltipContent
                          formatter={(v) => formatUsd(Number(v))}
                        />
                      }
                    />
                    <Bar
                      dataKey='cost'
                      radius={[0, 4, 4, 0]}
                      cursor={onToggleLabel ? 'pointer' : undefined}
                      onClick={
                        onToggleLabel
                          ? (data) => {
                              const id = (data as { payload?: { id?: string } })
                                .payload?.id;
                              if (id) onToggleLabel(id);
                            }
                          : undefined
                      }
                    >
                      {labelSpendData.map((entry) => {
                        const isDimmed =
                          selectedLabelSet.size > 0 &&
                          !selectedLabelSet.has(entry.id);
                        return (
                          <Cell
                            key={entry.id}
                            fill={entry.color}
                            fillOpacity={isDimmed ? LABEL_BAR_DIM_OPACITY : 1}
                            stroke={
                              selectedLabelSet.has(entry.id)
                                ? entry.color
                                : undefined
                            }
                            strokeWidth={selectedLabelSet.has(entry.id) ? 2 : 0}
                          />
                        );
                      })}
                    </Bar>
                  </BarChart>
                </ChartContainer>
              ) : (
                <div className='flex min-h-[250px] flex-1 items-center justify-center rounded-md border border-dashed border-border/60 bg-muted/20 px-4 text-center text-sm text-muted-foreground'>
                  No label spend in this period
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {langfuseAvailable && metrics.top_users.length > 0 && (
          <Card className='flex h-full flex-col'>
            <CardHeader>
              <CardTitle className='flex items-center gap-2 text-base'>
                <Users className='size-4' />
                Top Users by Consumption
              </CardTitle>
            </CardHeader>
            <CardContent className='flex min-h-0 flex-1 flex-col'>
              <div className='min-h-[250px] flex-1 space-y-3'>
                {metrics.top_users.map((u, i) => (
                  <div key={i} className='flex items-center justify-between'>
                    <span className='max-w-[200px] truncate text-sm'>
                      {u.user_id}
                    </span>
                    <span className='text-sm font-medium text-muted-foreground'>
                      {formatTokens(u.tokens)}
                    </span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
